import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ShieldCheck, KeyRound, Lock } from "lucide-react";

export default function AdminLogin() {
  const { setAdminAuth, sessionMessage } = useAdminAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | setup
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [setupForm, setSetupForm] = useState({ code: "", password: "", confirm: "" });
  const [adminHint, setAdminHint] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Only ever set when the backend rejected THIS device's session — not for a
  // network hiccup. Arriving back here with `reason=expired` means exactly that.
  const location = useLocation();
  const expiredReason = location?.state?.reason === "expired" ? sessionMessage : "";

  useEffect(() => {
    api.get("/auth/admin/setup-status").then(({ data }) => {
      if (data.needsSetup) setMode("setup");
      if (data.adminHint) setAdminHint(data.adminHint);
    }).catch(() => {});
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", form);
      setChallenge(data.challengeToken);
      if (data.mfaSetupRequired) setSetup({ secret: data.secret, otpauthUri: data.otpauthUri });
      setStep(2);
    } catch (e) {
      if (e?.response?.status === 403) setMode("setup");
      setError(apiError(e));
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/verify-mfa", { challengeToken: challenge, code });
      setAdminAuth(data.token, data.user);
      navigate("/admin/dashboard");
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  const requestCode = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/request-setup-code", {});
      setCodeSent(true);
      toast.success(data.message || "Setup code emailed to the admin inbox.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError("");
    if (setupForm.code.trim().length !== 6) { setError("Enter the 6-digit code from your email."); return; }
    if (setupForm.password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (setupForm.password !== setupForm.confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/admin/setup", { code: setupForm.code.trim(), password: setupForm.password });
      toast.success("Password set. You can now sign in.");
      setMode("login"); setStep(1); setForm({ email: "", password: "" });
      setSetupForm({ code: "", password: "", confirm: "" }); setCodeSent(false);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 grid place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <span className="inline-grid h-12 w-12 rounded-xl bg-amber-500 text-slate-900 place-items-center font-display font-bold text-xl mb-3">S</span>
          <h1 className="text-2xl font-bold text-white font-display">Admin Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Shree Shyam Mobile · Secure sign in</p>
        </div>

        {mode === "setup" ? (
          <form onSubmit={submitSetup} className="bg-white rounded-2xl p-6 space-y-4" data-testid="admin-setup-form">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> {error}</p>}
            <div className="flex items-center gap-2 text-slate-800"><Lock className="h-5 w-5 text-amber-500" /><span className="font-semibold">First-time setup / reset</span></div>
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
              We'll email a one-time 6-digit code to the authorized admin inbox{adminHint ? <> (<b>{adminHint}</b>)</> : ""}. Click <b>Email me a setup code</b>, open that inbox, then enter the code and choose your own strong password. The code expires in 10 minutes.
            </div>
            <button type="button" onClick={requestCode} disabled={loading} className="w-full border border-slate-200 text-slate-700 font-semibold py-2.5 rounded-lg text-sm hover:border-amber-400 disabled:opacity-60" data-testid="generate-setup-code">{codeSent ? "Resend setup code" : "Email me a setup code"}</button>
            {codeSent && <p className="text-xs text-emerald-600 text-center">Code sent — check your email inbox (and spam).</p>}
            <div>
              <label className="text-sm font-medium text-slate-700">6-digit Code (from email)</label>
              <input required inputMode="numeric" value={setupForm.code} onChange={(e) => setSetupForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))} data-testid="setup-token" placeholder="000000"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-center text-xl font-mono tracking-[0.4em]" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">New Password (min 10 chars)</label>
              <input type="password" required value={setupForm.password} onChange={(e) => setSetupForm((f) => ({ ...f, password: e.target.value }))} data-testid="setup-password"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Confirm Password</label>
              <input type="password" required value={setupForm.confirm} onChange={(e) => setSetupForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="setup-confirm"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
            </div>
            <button disabled={loading} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg disabled:opacity-60" data-testid="setup-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Set Password
            </button>
            <button type="button" onClick={() => { setMode("login"); setError(""); }} className="w-full text-sm text-slate-500">← Back to sign in</button>
          </form>
        ) : step === 1 ? (
          <form onSubmit={submitPassword} className="bg-white rounded-2xl p-6 space-y-4">
            {expiredReason && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-center gap-2" data-testid="admin-login-session-notice">
                <ShieldAlert className="h-4 w-4" /> {expiredReason}
              </p>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> {error}</p>}
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-email"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input type="password" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="admin-password"
                className="w-full h-11 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-sm" />
            </div>
            <button disabled={loading} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg disabled:opacity-60" data-testid="admin-login-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Continue
            </button>
            <button type="button" onClick={() => setMode("setup")} className="w-full text-sm text-amber-600 font-medium" data-testid="goto-setup">First-time setup / forgot password?</button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="bg-white rounded-2xl p-6 space-y-4">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> {error}</p>}
            <div className="flex items-center gap-2 text-slate-700"><ShieldCheck className="h-5 w-5 text-emerald-500" /><span className="text-sm font-medium">Two-step verification</span></div>
            {setup && (
              <div className="text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-2">
                <p className="font-semibold text-slate-700">First-time authenticator setup</p>
                <p>Add this secret to your authenticator app (Google Authenticator / Authy):</p>
                <p className="font-mono font-semibold text-slate-900 break-all select-all">{setup.secret}</p>
                <p>Then enter the current 6-digit code below.</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><KeyRound className="h-4 w-4" /> Authenticator Code</label>
              <input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="admin-mfa-code"
                className="w-full h-12 px-3 mt-1 rounded-lg border border-slate-200 outline-none focus:border-amber-400 text-center text-2xl font-mono tracking-[0.4em]" />
            </div>
            <button disabled={loading || code.length !== 6} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg disabled:opacity-60" data-testid="admin-mfa-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Verify & Sign In
            </button>
            <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); }} className="w-full text-sm text-slate-500">← Back</button>
          </form>
        )}
      </div>
    </div>
  );
}
